import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import Lenis from "lenis";
import { Phone, Menu, X, ArrowRight, ArrowUpRight, Hammer, Home, Layers, BookOpen, Star, MapPin, Mail, Clock, Grid3X3 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import ServicePage from "./ServicePage";
import SeoServicePage from "./SeoServicePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component using user's uploaded logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/lkf2d39x_530DA30B-6625-4EFA-99D8-BF45692B5D4C.png";

const Logo = ({ className = "", size = "default" }) => (
  <img 
    src={LOGO_URL} 
    alt="Wagner Carpentry & Millwork" 
    className={`${size === "small" ? "h-14" : size === "large" ? "h-32" : "h-[124px]"} w-auto ${className}`}
  />
);

// Services data
const services = [
  {
    id: "finishing",
    slug: "finishing-carpentry",
    title: "Finishing Carpentry",
    description: "Crown moulding, baseboards, and precision trim work.",
    icon: Hammer,
    spec: "001"
  },
  {
    id: "renovation",
    slug: "renovation-services",
    title: "Renovation",
    description: "Complete transformations from concept to completion.",
    icon: Home,
    spec: "002"
  },
  {
    id: "framing",
    slug: "framing",
    title: "Framing",
    description: "Structural framing for residential and commercial.",
    icon: Layers,
    spec: "003"
  },
  {
    id: "cabinetry",
    slug: "custom-cabinetry",
    title: "Custom Cabinetry",
    description: "Bespoke storage solutions tailored to your space.",
    icon: BookOpen,
    spec: "004"
  },
  {
    id: "accent-walls",
    slug: "accent-walls",
    title: "Accent Walls",
    description: "Slat panels, wood features & acoustic wall solutions.",
    icon: Layers,
    spec: "005"
  }
];

// Testimonials
const testimonials = [
  { id: 1, name: "Sarah M.", location: "Vancouver, BC", text: "Exceptional attention to detail. The custom cabinets exceeded all expectations.", rating: 5 },
  { id: 2, name: "Michael C.", location: "Burnaby, BC", text: "Professional and precise. Our trim work is absolutely beautiful.", rating: 5 },
  { id: 3, name: "Jennifer R.", location: "North Van, BC", text: "Quality craftsmanship from start to finish. Highly recommend.", rating: 5 }
];

// Google Reviews Section
const GoogleReviewsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const googleReviews = [
    { name: "HomeStars Review", text: "Amazing - kind, prompt and high quality. Would definitely hire again!", rating: 5, source: "HomeStars" },
  ];

  return (
    <section className="py-16 md:py-24 bg-stone-900 text-stone-50" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-heading text-3xl md:text-4xl font-bold">Google Reviews</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="font-heading text-5xl font-bold text-amber-500">5.0</span>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-amber-500 text-amber-500" />
              ))}
            </div>
          </div>
          <p className="text-stone-400 font-mono text-sm tracking-wider uppercase">Perfect Rating on Google</p>
        </motion.div>

        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <blockquote className="text-xl md:text-2xl text-stone-200 italic mb-6">
            "Amazing - kind, prompt and high quality. Would definitely hire again!"
          </blockquote>
          <p className="text-stone-500 font-mono text-xs tracking-wider uppercase mb-8">— Verified Customer Review</p>
          
          <a
            href="https://maps.app.goo.gl/aJ1qLzHyFuaoLEYt8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-stone-50 text-stone-900 px-8 py-4 font-mono text-xs tracking-wider uppercase hover:bg-amber-500 hover:text-white transition-colors"
            data-testid="google-reviews-link"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            See All Reviews on Google
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Vancouver Services Section (SEO Internal Links)
const VancouverServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const vancouverServices = [
    {
      url: "/custom-built-ins-vancouver",
      title: "Custom Built-Ins Vancouver",
      description: "Living room wall units, fireplace surrounds, home office storage",
      icon: Grid3X3
    },
    {
      url: "/feature-walls-vancouver",
      title: "Feature Walls Vancouver",
      description: "Wood slat walls, panel walls, TV feature walls, accent walls",
      icon: Layers
    },
    {
      url: "/finish-carpentry-vancouver",
      title: "Finish Carpentry Vancouver",
      description: "Baseboards, crown molding, trim & interior millwork",
      icon: Hammer
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-stone-100 border-t-2 border-stone-200" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 block">Metro Vancouver</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">Premium Carpentry Services</h2>
          <p className="text-stone-600 max-w-2xl mx-auto">Serving Vancouver, North Vancouver, West Vancouver, Burnaby, Coquitlam, Port Moody & Richmond</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {vancouverServices.map((service, index) => (
            <motion.div
              key={service.url}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={service.url}
                className="block p-8 bg-white border-2 border-stone-200 hover:border-stone-900 transition-colors group h-full"
              >
                <div className="w-12 h-12 border-2 border-stone-300 flex items-center justify-center mb-6 group-hover:border-amber-700 group-hover:bg-amber-50 transition-colors">
                  <service.icon className="w-5 h-5 text-stone-600 group-hover:text-amber-700" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3 group-hover:text-amber-700 transition-colors">
                  {service.title}
                </h3>
                <p className="text-stone-600 text-sm mb-4">{service.description}</p>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 group-hover:text-stone-900">
                  Learn More <ArrowUpRight className="w-3 h-3" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery
const gallery = [
  { id: 1, src: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/0xajseg5_IMG_3181.webp", label: "Aritzia / Guildford" },
  { id: 2, src: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/50f15bfb_2E454F33-151B-4C03-8769-00FE696C8195.png", label: "Accent Wall / 2024" },
  { id: 3, src: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/1f6ppp4q_IMG_3184.jpeg", label: "Custom Ceiling / 2024" },
  { id: 4, src: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/wc3omeng_IMG_3202.jpeg", label: "Slat Wall / 2024" },
  { id: 5, src: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/9i38racx_IMG_3203.jpeg", label: "Feature Wall / 2024" }
];

// Navigation
const Navigation = ({ scrolled }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-stone-50/95 backdrop-blur-sm border-b-2 border-stone-200" : "bg-transparent"}`}>
      <div className="max-w-screen-2xl mx-auto px-3 md:px-6">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => scrollTo("hero")} data-testid="logo-link" className="mt-2">
            <Logo />
          </button>

          <div className="hidden lg:flex items-center gap-10">
            {["services", "work", "about", "contact"].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item)}
                className="font-mono text-xs tracking-[0.15em] uppercase text-stone-600 hover:text-stone-900 transition-colors"
                data-testid={`nav-${item}`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <a
              href="tel:+16049064058"
              className="hidden md:flex items-center gap-2 bg-stone-900 text-stone-50 border-2 border-stone-900 px-6 py-3 font-mono text-xs tracking-wider uppercase hard-shadow btn-click transition-all"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              604-906-4058
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 border-2 border-stone-900 flex items-center justify-center"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-stone-50 border-b-2 border-stone-200"
        >
          <div className="px-6 py-8 space-y-6">
            {["services", "work", "about", "contact"].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item)}
                className="block font-mono text-sm tracking-[0.15em] uppercase text-stone-700 hover:text-stone-900"
                data-testid={`mobile-nav-${item}`}
              >
                {item}
              </button>
            ))}
            <a
              href="tel:+16049064058"
              className="flex items-center justify-center gap-2 bg-stone-900 text-stone-50 border-2 border-stone-900 px-6 py-4 font-mono text-xs tracking-wider uppercase w-full mt-6"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero - Split Layout
const HeroSection = () => {
  const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="min-h-screen pt-20">
      <div className="max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
          {/* Left - Typography */}
          <div className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-0 lg:border-r-2 border-stone-200">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-6 block">
                Est. Vancouver, BC
              </span>
              
              <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold tracking-tight text-stone-900 leading-[0.9] mb-8">
                Craft.<br />
                <span className="text-stone-400">Precision.</span><br />
                Legacy.
              </h1>

              <p className="text-stone-600 text-lg max-w-md mb-10 leading-relaxed">
                Bespoke carpentry and millwork for discerning clients across the Lower Mainland.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToContact}
                  className="bg-stone-900 text-stone-50 border-2 border-stone-900 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hard-shadow btn-click hover:bg-stone-800"
                  data-testid="hero-quote-btn"
                >
                  Request Quote
                  <ArrowRight className="ml-3 w-4 h-4" />
                </Button>
                <a
                  href="tel:+16049064058"
                  className="inline-flex items-center justify-center gap-2 bg-transparent text-stone-900 border-2 border-stone-300 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:border-stone-900 transition-colors"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  604-906-4058
                </a>
              </div>

              {/* Stats row */}
              <div className="flex gap-12 mt-16 pt-8 border-t-2 border-stone-200">
                {[
                  { value: "10+", label: "Years" },
                  { value: "100+", label: "Projects" },
                  { value: "100%", label: "Satisfaction" }
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="font-heading text-3xl font-semibold text-stone-900">{stat.value}</p>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative overflow-hidden bg-stone-200"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/qlc4ebdd_93BBC744-FCA6-44F6-9C37-8C846A0FABCC.png"
              alt="Wagner Carpentry"
              className="w-full h-full object-cover img-zoom"
            />
            {/* Overlay label */}
            <div className="absolute bottom-8 left-8 bg-stone-900 text-stone-50 px-6 py-4">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase">Lower Mainland, BC</p>
              <p className="font-heading text-lg mt-1">Since 2018</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services - Bento Grid
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="services" className="py-24 md:py-32 border-t-2 border-stone-200" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 block">Services</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-stone-900">What We Build</h2>
          </div>
          <p className="text-stone-600 max-w-md">Comprehensive carpentry solutions for residential and commercial projects.</p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-stone-200">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`p-8 md:p-10 border-stone-200 border-b-2 last:border-b-0 md:odd:border-r-2 lg:border-r-2 lg:[&:nth-child(3n)]:border-r-0 lg:last:border-b-0 lg:[&:nth-child(4)]:border-b-0 lg:[&:nth-child(5)]:border-b-0 group hover:bg-stone-100 transition-colors`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 border-2 border-stone-300 flex items-center justify-center group-hover:border-stone-900 transition-colors">
                  <service.icon className="w-5 h-5 text-stone-600" />
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] text-stone-400">{service.spec}</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{service.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-6">{service.description}</p>
              <Link 
                to={`/services/${service.slug}`}
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 group-hover:text-stone-900 transition-colors"
              >
                Learn more <ArrowUpRight className="w-3 h-3" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery / Work Section
const WorkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="work" className="py-24 md:py-32 bg-stone-100 border-t-2 border-stone-200" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 block">Portfolio</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-stone-900">Selected Work</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {gallery.map((item, index) => (
            <motion.div
              key={item.id}
              className="group relative overflow-hidden border-2 border-stone-200 bg-white"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`gallery-item-${item.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={item.src}
                  alt={item.label}
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-stone-900 text-stone-50 px-6 py-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="font-mono text-xs tracking-[0.15em] uppercase">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-24 md:py-32 border-t-2 border-stone-200" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="border-2 border-stone-200 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/ezx546nr_B73DE578-E0A6-46F5-93A8-FE3CCCE05565.png"
                alt="Wagner Carpentry at work"
                className="w-full aspect-[4/3] object-cover img-zoom"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white px-8 py-6 hard-shadow">
              <p className="font-heading text-4xl font-semibold">10+</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase mt-1">Years</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 block">About</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-6">
              Built on<br />Craftsmanship
            </h2>
            <div className="w-16 h-0.5 bg-amber-700 mb-8" />
            <p className="text-stone-600 text-lg leading-relaxed mb-6">
              Wagner Carpentry & Millwork is a premier provider of carpentry services in the Lower Mainland. We specialize in custom hemlock ceilings, wooden wall panels, baseboards, crown moulding, and all types of trim carpentry.
            </p>
            <p className="text-stone-600 leading-relaxed mb-10">
              Our team brings decades of combined experience to every project, ensuring exceptional quality and attention to detail.
            </p>

            {/* Testimonial */}
            <div className="border-l-2 border-amber-700 pl-6">
              <p className="text-stone-700 italic mb-4">"{testimonials[0].text}"</p>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-amber-600 text-amber-600" />
                  ))}
                </div>
                <span className="font-mono text-[10px] tracking-[0.15em] text-stone-500">— {testimonials[0].name}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact - Work Order Style
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", project_type: "", message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.project_type || !formData.message) {
      toast.error("Please complete all required fields");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/quote`, formData);
      toast.success(response.data.message);
      setFormData({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (error) {
      toast.error("Submission failed. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-stone-100 border-t-2 border-stone-200" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 block">Contact</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-6">
              Start Your<br />Project
            </h2>
            <div className="w-16 h-0.5 bg-amber-700 mb-8" />
            <p className="text-stone-600 text-lg leading-relaxed mb-12">
              Ready to transform your space? Submit your project details and we'll respond within 24 hours.
            </p>

            <div className="space-y-8">
              <a href="tel:+16049064058" className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="w-12 h-12 border-2 border-stone-300 flex items-center justify-center group-hover:border-stone-900 group-hover:bg-stone-900 transition-all">
                  <Phone className="w-5 h-5 text-stone-600 group-hover:text-stone-50" />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-stone-900">604-906-4058</p>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-stone-500 uppercase">Mon-Fri 7am-6pm</p>
                </div>
              </a>
              <a href="mailto:info@wagnercarpentry.com" className="flex items-start gap-4 group" data-testid="contact-email">
                <div className="w-12 h-12 border-2 border-stone-300 flex items-center justify-center group-hover:border-stone-900 group-hover:bg-stone-900 transition-all">
                  <Mail className="w-5 h-5 text-stone-600 group-hover:text-stone-50" />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-stone-900">info@wagnercarpentry.com</p>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-stone-500 uppercase">Email anytime</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border-2 border-stone-300 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-stone-600" />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-stone-900">Lower Mainland</p>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-stone-500 uppercase">Vancouver • Burnaby • Surrey</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form - Work Order Style */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-white border-2 border-stone-200 p-8 md:p-12" data-testid="quote-form">
              <div className="flex items-center justify-between mb-10 pb-6 border-b-2 border-stone-200">
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-stone-900">Work Order</h3>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-stone-500 uppercase mt-1">Quote Request Form</p>
                </div>
                <span className="font-mono text-[10px] tracking-[0.15em] text-stone-400">#WC-{new Date().getFullYear()}</span>
              </div>

              <div className="space-y-8">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 block">Client Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="work-order-input w-full text-stone-900"
                    placeholder="Your full name"
                    data-testid="input-name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 block">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="work-order-input w-full text-stone-900"
                      placeholder="you@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 block">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="work-order-input w-full text-stone-900"
                      placeholder="(604) 000-0000"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 block">Service Type *</label>
                  <select
                    value={formData.project_type}
                    onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                    className="work-order-input w-full text-stone-900 bg-transparent cursor-pointer"
                    data-testid="select-project-type"
                  >
                    <option value="">Select service</option>
                    <option value="finishing">Finishing Carpentry</option>
                    <option value="renovation">Renovation Services</option>
                    <option value="framing">Framing</option>
                    <option value="cabinetry">Custom Cabinetry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 block">Project Description *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="work-order-input w-full text-stone-900 min-h-[120px] resize-none"
                    placeholder="Describe your project..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-stone-900 text-stone-50 border-2 border-stone-900 py-4 font-mono text-xs tracking-[0.15em] uppercase hard-shadow btn-click hover:bg-stone-800 transition-colors disabled:opacity-50"
                  data-testid="submit-quote-btn"
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="bg-stone-900 text-stone-50 py-16 md:py-24">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-2">
            <div className="mb-6">
              <img 
                src={LOGO_URL} 
                alt="Wagner Carpentry & Millwork" 
                className="h-[124px] w-auto brightness-0 invert"
              />
            </div>
            <p className="text-stone-400 max-w-sm leading-relaxed">
              Precision craftsmanship for the Lower Mainland. Building legacy through quality since 2018.
            </p>
          </div>

          <div>
            <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-6">Navigate</h4>
            <div className="space-y-3">
              {["services", "work", "about", "contact"].map((item) => (
                <button key={item} onClick={() => scrollTo(item)} className="block text-stone-300 hover:text-stone-50 transition-colors capitalize text-sm" data-testid={`footer-${item}`}>
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-6">Contact</h4>
            <div className="space-y-3 text-sm">
              <a href="tel:+16049064058" className="block text-stone-300 hover:text-stone-50 transition-colors" data-testid="footer-phone">604-906-4058</a>
              <a href="mailto:info@wagnercarpentry.com" className="block text-stone-300 hover:text-stone-50 transition-colors" data-testid="footer-email">info@wagnercarpentry.com</a>
              <p className="text-stone-400">Lower Mainland, BC</p>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-500 font-mono text-[10px] tracking-[0.15em] uppercase">© {new Date().getFullYear()} Wagner Carpentry. All rights reserved.</p>
          <a
            href="tel:+16049064058"
            className="flex items-center gap-2 bg-stone-50 text-stone-900 px-6 py-3 font-mono text-[10px] tracking-[0.15em] uppercase hard-shadow-sm btn-click"
            data-testid="footer-call-btn"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>
      </div>
    </footer>
  );
};

// Floating Call Button
const FloatingCallButton = () => (
  <a
    href="tel:+16049064058"
    className="fixed bottom-6 right-6 z-50 md:hidden w-14 h-14 bg-amber-700 text-white flex items-center justify-center hard-shadow"
    data-testid="floating-call-btn"
  >
    <Phone className="w-6 h-6" />
  </a>
);

// Home Page Component
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);

    return () => {
      lenis.destroy();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <Navigation scrolled={scrolled} />
      <main>
        <HeroSection />
        <ServicesSection />
        <WorkSection />
        <AboutSection />
        <VancouverServicesSection />
        <ContactSection />
        <GoogleReviewsSection />
      </main>
      <Footer />
      <FloatingCallButton />
    </>
  );
};

// App with Router
function App() {
  return (
    <BrowserRouter>
      <div className="font-body">
        <Toaster position="top-center" richColors duration={5000} closeButton />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/custom-built-ins-vancouver" element={<SeoServicePage slug="custom-built-ins-vancouver" />} />
          <Route path="/feature-walls-vancouver" element={<SeoServicePage slug="feature-walls-vancouver" />} />
          <Route path="/finish-carpentry-vancouver" element={<SeoServicePage slug="finish-carpentry-vancouver" />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
